import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HiOutlineDocumentText,
  HiOutlineCalendar,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineChartBar,
  HiOutlineBuildingOffice2,
  HiOutlineUserGroup,
  HiOutlineClipboardDocumentCheck,
  HiOutlineArrowLeft,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineDocumentArrowDown,
  HiOutlineShieldCheck,
  HiOutlineClock,
  HiOutlineClipboardDocumentList,
} from 'react-icons/hi2';
import { authService, plantService } from '../services';
import safetyObservationService from '../services/safetyObservationService';
import { usePermissions } from '../hooks/usePermissions';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import './Reports.css';

const SafetyObservationReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();
  const canView = hasPermission('SafetyObservationReport.View');

  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    plantIds: [],
    categoryId: '',
    status: '',
  });

  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    if (tenantId && canView) {
      loadInitialData();
    }
  }, [tenantId, canView]);

  const loadInitialData = async () => {
    try {
      const [plantsData, categoriesData] = await Promise.all([
        plantService.getAllPlants(),
        safetyObservationService.getCategories(),
      ]);
      const pData = plantsData || [];
      setPlants(pData);
      
      // Auto-select plant if only 1 exists
      if (pData.length === 1) {
        setFilters(prev => ({ ...prev, plantIds: [pData[0].plantId] }));
      }
      
      const uniqueCategories = [];
      const seenCategoryNames = new Set();
      (categoriesData || []).forEach(cat => {
        const name = cat.categoryName || cat.name;
        if (name && !seenCategoryNames.has(name.toLowerCase())) {
          seenCategoryNames.add(name.toLowerCase());
          uniqueCategories.push(cat);
        }
      });
      setCategories(uniqueCategories);
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handlePlantChange = (vals) => {
    setFilters(prev => ({ ...prev, plantIds: vals }));
  };

  const handleGenerateReport = async () => {
    if (!canView) return;
    showReportGeneratingToast();
    try {
      setLoading(true);
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        plantIds: filters.plantIds.length > 0 ? filters.plantIds : undefined,
        categoryId: filters.categoryId ? parseInt(filters.categoryId, 10) : undefined,
        status: filters.status || undefined,
      };

      let data;
      switch (activeTab) {
        case 'summary': data = await safetyObservationService.getObservationSummaryReport(params); break;
        case 'unsafe': data = await safetyObservationService.getUnsafeBehaviorReport(params); break;
        case 'category': data = await safetyObservationService.getCategoryWiseReport(params); break;
        case 'area': data = await safetyObservationService.getAreaDepartmentWiseReport(params); break;
        case 'observer': data = await safetyObservationService.getObserverPerformanceReport(params); break;
        case 'action': data = await safetyObservationService.getActionTrackingReport(params); break;
        default: return;
      }
      setReportData(data);
      reportGenerationDone();
    } catch (err) {
      console.error('Error generating report:', err);
      reportGenerationFailed('Could not generate report.');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;
    let exportData = [];
    let fileName = `SBO_Report_${activeTab}_${filters.startDate}`;
    // Export mapping logic... (omitted for brevity but kept consistent)
    // Actually keep it for completeness
    if (activeTab === 'summary') {
        exportData = [
          { Metric: 'Total Observations', Value: reportData.totalObservations },
          { Metric: 'Safe Observations', Value: reportData.safeObservations },
          { Metric: 'Unsafe Observations', Value: reportData.unsafeObservations },
          { Metric: 'Observers', Value: reportData.totalObservers },
          { Metric: 'Completion Rate', Value: reportData.observationCompletionRate + '%' },
          { Metric: 'Safe Ratio', Value: reportData.safeVsUnsafeRatio + '%' }
        ];
    } else if (activeTab === 'unsafe') {
        exportData = (reportData.unsafeBehaviors || []).map(item => ({
          Category: item.categoryName,
          SubCategory: item.subCategoryName,
          Frequency: item.frequency,
          Description: item.description,
          Departments: (item.departments || []).join(', '),
          Plants: (item.plants || []).join(', ')
        }));
    } else if (activeTab === 'category') {
        exportData = (reportData.categoryBreakdown || []).map(item => ({
          Category: item.categoryName,
          Total: item.totalObservations,
          Safe: item.totalSafe,
          Unsafe: item.totalUnsafe,
          'Unsafe %': item.unsafePercentage + '%'
        }));
    } else if (activeTab === 'area') {
        exportData = (reportData.departmentBreakdown || []).map(item => ({
          Department: item.departmentName,
          Rounds: item.totalRounds,
          Observations: item.totalObservations,
          Safe: item.safeObservations,
          Unsafe: item.unsafeObservations
        }));
    } else if (activeTab === 'observer') {
        exportData = (reportData.observerPerformance || []).map(item => ({
          Observer: item.observerName,
          Rounds: item.totalRounds,
          Observations: item.totalObservations,
          Safe: item.safeObservations,
          Unsafe: item.unsafeObservations
        }));
    } else if (activeTab === 'action') {
        exportData = (reportData.actions || []).map(item => ({
          Description: item.description,
          Status: item.status,
          AssignedTo: item.assignedTo,
          DueDate: item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A',
          Observation: item.observationNumber,
          Item: item.itemDescription
        }));
    }
    
    if (exportData.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(dataBlob, `${fileName}.xlsx`);
  };

  const tabs = [
    { id: 'summary', label: 'Observation Summary', icon: HiOutlineChartBar },
    { id: 'unsafe', label: 'Unsafe Behavior', icon: HiOutlineExclamationTriangle },
    { id: 'category', label: 'Category-wise', icon: HiOutlineClipboardDocumentList },
    { id: 'area', label: 'Area/Department', icon: HiOutlineBuildingOffice2 },
    { id: 'observer', label: 'Observer Performance', icon: HiOutlineUserGroup },
    { id: 'action', label: 'Action Tracking', icon: HiOutlineClipboardDocumentCheck },
  ];

  const ReportBarChart = ({ data, labelKey, valueKey, title, color = '#3b82f6' }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
    return (
      <div className="chart-card" style={{ marginTop: '24px', padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
        <h4 style={{ margin: '0 0 20px 0', fontSize: '1rem', color: '#1e293b' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {data.map((item, i) => {
            const val = Number(item[valueKey]) || 0;
            const percentage = (val / max) * 100;
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                  <span>{item[labelKey]}</span><span>{val}</span>
                </div>
                <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${percentage}%`, background: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const STAT_CARDS = reportData && activeTab === 'summary' ? [
    { label: "Total Observations", icon: HiOutlineChartBar, color: "#3b82f6", borderColor: "#3b82f6", count: reportData.totalObservations || 0, sub: "In period" },
    { label: "Safe Acts", icon: HiOutlineShieldCheck, color: SEMANTIC_SUCCESS_MID, borderColor: SEMANTIC_SUCCESS_MID, count: reportData.totalSafe || 0, sub: `${reportData.safeVsUnsafeRatio || 0}% Safe ratio` },
    { label: "Unsafe Acts", icon: HiOutlineExclamationTriangle, color: "#ef4444", borderColor: "#ef4444", count: reportData.totalUnsafe || 0, sub: "Requiring intervention" },
    { label: "Observers", icon: HiOutlineUserGroup, color: "#8b5cf6", borderColor: "#8b5cf6", count: reportData.totalObservers || 0, sub: "Active participation" },
  ] : [];

  if (!canView) {
    return (
      <div className="pagecontainer report-page">
        <div className="error-message">
          You do not have permission to view safety observation reports.
        </div>
      </div>
    );
  }

  return (
    <div className="pagecontainer report-page">
      <div className="page-header">
        <div className="oee-report-header-left">
          <button className="oee-report-back-btn" onClick={() => navigate("/home/reports")}>
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              {tabs.find((t) => t.id === activeTab)?.icon && React.createElement(tabs.find((t) => t.id === activeTab).icon)}
              {tabs.find(t => t.id === activeTab)?.label} Report
            </h1>
            <p className="oee-report-subtitle">Analyze safety observations and behavior patterns</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="oee-filter-toggle-btn" onClick={() => setShowFilters(p => !p)}>
            <HiOutlineCalendar size={16} /> Filters {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button className="btn-primary btn" onClick={exportToExcel} disabled={loading || !reportData}>
            <HiOutlineDocumentArrowDown /> Export
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group"><label>Start Date</label><input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="filter-input" /></div>
            <div className="filter-group"><label>End Date</label><input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="filter-input" /></div>
            <div className="filter-group">
              <label>Plants</label>
              <MultiSelectDropdown
                options={plants}
                selectedValues={filters.plantIds}
                onChange={handlePlantChange}
                placeholder="All Plants"
                displayKey="plantName"
                valueKey="plantId"
              />
            </div>
            <div className="filter-group">
              <label>Category</label>
              <select
                name="categoryId"
                value={filters.categoryId}
                onChange={handleFilterChange}
                className="filter-input"
              >
                <option value="">All Categories</option>
                {categories.map(c => (
                  <option key={c.categoryId || c.id} value={c.categoryId || c.id}>
                    {c.categoryName || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="filter-input"
              >
                <option value="">All Statuses</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
          </div>
          <div className="report-filter-actions">
            {(filters.plantId || filters.category || filters.status) && (
              <button 
                className="oee-clear-filters-btn" 
                onClick={() => {
                  setFilters({
                    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
                    endDate: new Date().toISOString().split('T')[0],
                    plantId: '',
                    category: '',
                    status: ''
                  });
                  setReportData(null);
                }}
              >
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={handleGenerateReport} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
        </div>
      )}

      {activeTab === 'summary' && reportData && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          {STAT_CARDS.map((card, idx) => (
            <div key={idx} style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", borderLeft: `4px solid ${card.borderColor}`, padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}><div style={{ padding: "6px", background: `${card.borderColor}18`, borderRadius: "6px", color: card.borderColor }}><card.icon size={18} /></div><p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>{card.label}</p></div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: card.color }}>{card.count}</h2><p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>{card.sub}</p>
            </div>
          ))}
        </div>
      )}

      <div className="reports-table">
        {loading ? <div className="loading-container">Loading data...</div> : !reportData ? (
          <div className="report-placeholder"><h3>Safety Observations</h3><p>Select criteria to view analytics</p></div>
        ) : (
          <div className="report-table-wrapper">
            {activeTab === 'summary' && <SummaryView data={reportData} />}
            {activeTab === 'unsafe' && <UnsafeView data={reportData} />}
            {activeTab === 'category' && <CategoryView data={reportData} />}
            {activeTab === 'area' && <AreaView data={reportData} />}
            {activeTab === 'observer' && <ObserverView data={reportData} />}
            {activeTab === 'action' && <ActionView data={reportData} />}
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryView = ({ data }) => (
    <div className="report-view-container">
        <h3>Observation Overview</h3>
        <p>Comprehensive breakdown of safety metrics across all active areas and departments during the selected period.</p>
        
        <table className="report-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '40%' }} />
            </colgroup>
            <thead>
                <tr>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Metric Type</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Value</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Distribution Meter</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td><strong>Safe acts vs Act Total</strong></td>
                    <td style={{ color: SEMANTIC_SUCCESS_MID, fontWeight: 700 }}>{data.safeObservations || 0} / {data.totalObservations || 0}</td>
                    <td>
                        <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${data.totalObservations > 0 ? (data.safeObservations / data.totalObservations) * 100 : 0}%`, background: SEMANTIC_SUCCESS_MID }} />
                        </div>
                    </td>
                </tr>
                <tr>
                    <td><strong>Unsafe acts vs Act Total</strong></td>
                    <td style={{ color: '#ef4444', fontWeight: 700 }}>{data.unsafeObservations || 0} / {data.totalObservations || 0}</td>
                    <td>
                        <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${data.totalObservations > 0 ? (data.unsafeObservations / data.totalObservations) * 100 : 0}%`, background: '#ef4444' }} />
                        </div>
                    </td>
                </tr>
                <tr>
                    <td><strong>Observer Participation</strong></td>
                    <td style={{ color: '#3b82f6', fontWeight: 700 }}>{data.totalObservers || 0} / {data.totalUsers || 0}</td>
                    <td>
                        <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${data.employeesObservedPercent || 0}%`, background: '#3b82f6' }} />
                        </div>
                    </td>
                </tr>
                <tr>
                    <td><strong>Completion Rate</strong></td>
                    <td style={{ color: '#8b5cf6', fontWeight: 700 }}>{data.observationCompletionRate || 0}%</td>
                    <td>
                        <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${data.observationCompletionRate || 0}%`, background: '#8b5cf6' }} />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
);

const UnsafeView = ({ data }) => (
    <div className="report-view-container">
        <h3>Top Unsafe Behaviors</h3>
        <table className="report-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '65%' }} />
            </colgroup>
            <thead>
                <tr>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Category</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Freq</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Description</th>
                </tr>
            </thead>
            <tbody>
                {(data.unsafeBehaviors || []).map((item, idx) => (
                    <tr key={idx}>
                        <td><strong>{item.categoryName}</strong></td>
                        <td style={{ textAlign: 'center' }}><span style={{ padding: '4px 8px', background: '#fee2e2', color: '#ef4444', borderRadius: '4px', fontWeight: 700 }}>{item.frequency}</span></td>
                        <td style={{ fontSize: '0.85rem', color: '#475569' }}>{item.description}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const CategoryView = ({ data }) => {
    const tableData = data.categoryBreakdown || [];
    const maxObservations = Math.max(...tableData.map(item => item.totalObservations), 1);
    
    return (
        <div className="report-view-container">
            <h3>Category Breakdown</h3>
            <table className="report-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Category Name</th>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Total</th>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Observation Distribution</th>
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((item, idx) => (
                        <tr key={idx}>
                            <td><strong>{item.categoryName}</strong></td>
                            <td style={{ textAlign: 'center' }}><strong>{item.totalObservations}</strong></td>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${(item.totalObservations / maxObservations) * 100}%`, background: '#3b82f6' }} />
                                    </div>
                                    <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                        {item.totalSafe} Safe / {item.totalUnsafe} Unsafe
                                    </span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const AreaView = ({ data }) => {
    const tableData = data.departmentBreakdown || [];
    const maxObservations = Math.max(...tableData.map(item => item.totalObservations), 1);

    return (
        <div className="report-view-container">
            <h3>Departmental Activity</h3>
            <table className="report-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Department</th>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Total</th>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Observation Load</th>
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((item, idx) => (
                        <tr key={idx}>
                            <td><strong>{item.departmentName}</strong></td>
                            <td style={{ textAlign: 'center' }}><strong>{item.totalObservations}</strong></td>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${(item.totalObservations / maxObservations) * 100}%`, background: '#8b5cf6' }} />
                                    </div>
                                    <span style={{ fontSize: '11px', color: '#64748b', width: '60px' }}>
                                        {item.totalRounds} Rounds
                                    </span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ObserverView = ({ data }) => {
    const tableData = data.observerPerformance || [];
    const maxObservations = Math.max(...tableData.map(item => item.totalObservations), 1);

    return (
        <div className="report-view-container">
            <h3>Observer Participation</h3>
            <table className="report-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Observer</th>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Acts</th>
                        <th style={{ background: '#f8fafc', color: '#64748b' }}>Contribution Meter</th>
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((item, idx) => (
                        <tr key={idx}>
                            <td><strong>{item.observerName}</strong></td>
                            <td style={{ textAlign: 'center' }}><strong>{item.totalObservations}</strong></td>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${(item.totalObservations / maxObservations) * 100}%`, background: SEMANTIC_SUCCESS_MID }} />
                                    </div>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                                        {item.totalRounds} Rounds Logged
                                    </span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ActionView = ({ data }) => (
    <div className="report-view-container">
        <h3>Action Item Tracking</h3>
        <table className="report-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
                <tr>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Action Item</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Status</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Assigned To</th>
                    <th style={{ background: '#f8fafc', color: '#64748b' }}>Due Date</th>
                </tr>
            </thead>
            <tbody>
                {(data.actions || []).map((item, idx) => (
                    <tr key={idx}>
                        <td style={{ fontSize: '0.85rem' }}>{item.description}</td>
                        <td style={{ textAlign: 'center' }}>
                            <span style={{ 
                                padding: '4px 10px', 
                                borderRadius: '20px', 
                                fontSize: '11px', 
                                fontWeight: 600,
                                background: item.status === 'Open' ? '#fee2e2' : '#dcfce7',
                                color: item.status === 'Open' ? '#ef4444' : SEMANTIC_SUCCESS_MID
                            }}>
                                {item.status}
                            </span>
                        </td>
                        <td>{item.assignedTo}</td>
                        <td style={{ fontSize: '0.85rem' }}>{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default SafetyObservationReports;
